import { useOverlayActions } from '../../../contexts/OverlayProvider'
import { OVERLAY_DURATION_MS } from '@/features/common/constants'
import { useCartKeyContext } from '@/features/pos/components/Cart/contexts/CartKey'
import {
    usePhoneFormAsyncSubmit,
    useResetForm,
} from '@/features/pos/components/OrderFinalization/form'
import { PhoneFormOutput } from '@/features/pos/components/OrderFinalization/form/types'
import { useCreatePhoneOrder } from '@/features/pos/components/OrderFinalization/hooks/useCreatePhoneOrder'
import { useCancellableDelay } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/hooks/useCancellableDelay'
import { useClearOrder } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/hooks/useClearOrder'
import { PaymentType } from '@/features/pos/components/OrderFinalization/parts/BottomSheet/types'
import { transformFormToPhoneOrderCreateInput } from '@/features/pos/components/OrderFinalization/utils/transforms'
import { usePosMode } from '@/features/pos/contexts/PosMode'
import { router } from 'expo-router'
import { useRef } from 'react'
import { useTranslation } from 'react-i18next'
import invariant from 'tiny-invariant'

export const usePrepareOrder = (
    shouldAttachAutomaticContainers: boolean
): ((paymentType: PaymentType) => Promise<void>) => {
    const { cartKey } = useCartKeyContext()

    const createPhoneOrder = useCreatePhoneOrder()

    const clearOrder = useClearOrder()

    const mode = usePosMode()

    const reset = useResetForm()

    const inFlight = useRef(false)

    const { setSuccess, unset } = useOverlayActions()

    const { wait, isCancelled } = useCancellableDelay()

    const { t } = useTranslation()

    return usePhoneFormAsyncSubmit(async (form: PhoneFormOutput, paymentType: PaymentType) => {
        if (inFlight.current) return
        inFlight.current = true

        invariant(cartKey, 'cartKey must be set')

        const orderId = await createPhoneOrder(
            cartKey,
            transformFormToPhoneOrderCreateInput(form, paymentType, shouldAttachAutomaticContainers)
        )

        if (!orderId) return

        const dest = mode === 'pos' ? '/pos/1-product-catalog' : '/phone/1-product-catalog'

        setSuccess(t('order.detail.accepted'))

        await wait(OVERLAY_DURATION_MS)

        if (isCancelled()) {
            clearOrder()
            reset()
            return
        }

        unset()

        router.replace(dest)

        clearOrder()

        reset()

        inFlight.current = false
    })
}
